import { ReactElement, useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  Autocomplete,
  createFilterOptions,
  useMediaQuery,
  useTheme,
  InputAdornment,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { fr } from "date-fns/locale/fr";
import { format, parse } from "date-fns";
import { toast } from "react-toastify";
import { useLanguage } from "../../context/LanguageContext";
import useUpdatePersonREST, { UpdatePersonRequest } from "../../hooks/Person/useUpdatePersonREST";
import { UserDataResponse } from "../../types/api";
import { WAY_TYPES } from "../../constants/wayTypes";
import { PHONE_CODES, COUNTRIES } from "../../constants/phoneCodes";
import { checkPostalCode } from "../../services/postalService";

interface EditAccountDialogProps {
  open: boolean;
  onClose: () => void;
  personData: UserDataResponse | null;
  entityIdentifier: string;
  onSuccess: () => void;
}

const EditAccountDialog = ({
  open,
  onClose,
  personData,
  entityIdentifier,
  onSuccess,
}: EditAccountDialogProps): ReactElement => {
  const { t } = useLanguage();
  const { updatePerson, loading, error } = useUpdatePersonREST();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthDate, setBirthDate] = useState<Date | null>(null);
  const [title, setTitle] = useState<number>(1);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const [email, setEmail] = useState("");
  const [emailCompanySolicitation, setEmailCompanySolicitation] = useState(true);
  const [emailTrustedSolicitation, setEmailTrustedSolicitation] = useState(false);

  const [phoneDialingCode, setPhoneDialingCode] = useState("33");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneBrand, setPhoneBrand] = useState("");
  const [phoneModel, setPhoneModel] = useState("");
  const [phoneCompanySolicitation, setPhoneCompanySolicitation] = useState(true);
  const [phoneTrustedSolicitation, setPhoneTrustedSolicitation] = useState(false);

  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [address3Name, setAddress3Name] = useState("");
  const [address3Num, setAddress3Num] = useState<number | "">(0);
  const [address3Type, setAddress3Type] = useState<number>(5);
  const [address4, setAddress4] = useState("");
  const [city, setCity] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [country, setCountry] = useState("FRANCE");
  const [addressCompanySolicitation, setAddressCompanySolicitation] = useState(true);
  const [addressTrustedSolicitation, setAddressTrustedSolicitation] = useState(false);

  const [citySuggestions, setCitySuggestions] = useState<string[]>([]);
  const [postalLoading, setPostalLoading] = useState(false);

  const [identifierValue, setIdentifierValue] = useState("");
  const [identifierType, setIdentifierType] = useState<number>(1);

  // Debounce sur le code postal → suggestions de ville (5 chiffres uniquement)
  useEffect(() => {
    if (!/^\d{5}$/.test(zipCode)) {
      setCitySuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setPostalLoading(true);
      try {
        const result = await checkPostalCode(zipCode);
        if (result?.address) {
          const allCities = [
            result.address.city,
            ...result.address.addressCompletionSuggestions.map((s) => s.city),
          ];
          const unique = Array.from(new Set(allCities.filter(Boolean)));
          setCitySuggestions(unique);
          setCity((prev) => (prev === '' ? unique[0] : prev));
        } else {
          setCitySuggestions([]);
        }
      } finally {
        setPostalLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [zipCode]);

  const REQUIRED_TOAST_ID = 'account-required-fields';
  const skipNextToastCheck = useRef(false);

  // Quand le dialog s'ouvre, on prépare le saut du premier cycle (chargement initial)
  // Quand il se ferme, on dismiss le toast
  useEffect(() => {
    if (open) {
      skipNextToastCheck.current = true;
    } else {
      skipNextToastCheck.current = false;
      toast.dismiss(REQUIRED_TOAST_ID);
    }
  }, [open]);

  // Toast déclenché uniquement sur changement des champs (pas à l'ouverture, ni quand fermé)
  useEffect(() => {
    if (!open) return;
    if (skipNextToastCheck.current) {
      skipNextToastCheck.current = false;
      return;
    }
    const hasEmpty = !firstName || !lastName || !email;
    if (hasEmpty) {
      toast.warn(t('pages.account.requiredFields'), {
        toastId: REQUIRED_TOAST_ID,
        autoClose: false,
        closeOnClick: false,
        draggable: false,
      });
    } else {
      toast.dismiss(REQUIRED_TOAST_ID);
    }
  }, [open, firstName, lastName, email, t]);

  useEffect(() => {
    if (personData && open) {
      const personResponse = personData.PersonResponse;
      const person = personResponse?.PersonResponse;
      const contacts = personResponse?.ContactsResponse;
      const address = contacts?.AddressResponse;
      const emailData = contacts?.EmailResponse;
      const cellPhone = contacts?.CellPhoneResponse;
      
      const identifiers = personResponse?.IdentifierResponse || [];
      const identifier = identifiers.length > 0 ? identifiers[identifiers.length - 1] : undefined;

      setFirstName(person?.firstName || "");
      setLastName(person?.lastName || "");
      if (person?.birthDate) {
        try {
          const parsedDate = parse(person.birthDate, "dd/MM/yyyy", new Date());
          setBirthDate(parsedDate);
        } catch {
          setBirthDate(null);
        }
      } else {
        setBirthDate(null);
      }
      setTitle(person?.title || 1);

      setEmail(emailData?.emailValue || "");
      setEmailCompanySolicitation(emailData?.allowCompanySolicitation === 1);
      setEmailTrustedSolicitation(emailData?.allowTrustedSolicitation === 1);

      setPhoneDialingCode(cellPhone?.dialingCode || "33");
      setPhoneNumber(cellPhone?.number || "");
      setPhoneBrand(cellPhone?.brand || "");
      setPhoneModel(cellPhone?.model || "");
      setPhoneCompanySolicitation(cellPhone?.allowCompanySolicitation === 1);
      setPhoneTrustedSolicitation(cellPhone?.allowTrustedSolicitation === 1);

      setAddress1(address?.address1 || "");
      setAddress2(address?.address2 || "");
      setAddress3Name(address?.address3Name || "");
      setAddress3Num(address?.address3Num ? Number(address.address3Num) : "");
      setAddress3Type(Number(address?.address3Type) || 5);
      setAddress4(address?.address4 || "");
      setCity(address?.city || "");
      setZipCode(address?.zipCode || "");
      setCountry(address?.country || "FRANCE");
      setAddressCompanySolicitation(address?.allowCompanySolicitation === 1);
      setAddressTrustedSolicitation(address?.allowTrustedSolicitation === 1);

      setIdentifierValue(identifier?.identifierValue || "");
      setIdentifierType(identifier?.identifierType || 1);
    }
  }, [personData, open]);

  const handleSubmit = async () => {
    const requestData: UpdatePersonRequest = {
      ContactsRequest: {
        AddressRequest: {
          allowCompanySolicitation: addressCompanySolicitation ? 1 : 0,
          allowTrustedSolicitation: addressTrustedSolicitation ? 1 : 0,
          address1: address1 || "",
          address2: address2 || "",
          address3Name: address3Name || "",
          address3Num: address3Num ? Number(address3Num) : 0,
          address3Type: address3Type || 5,
          address4: address4 || "",
          city,
          country,
          zipCode,
        },
        EmailRequest: {
          allowCompanySolicitation: emailCompanySolicitation ? 1 : 0,
          allowTrustedSolicitation: emailTrustedSolicitation ? 1 : 0,
          emailValue: email,
        },
        CellPhoneRequest: {
          allowCompanySolicitation: phoneCompanySolicitation ? 1 : 0,
          allowTrustedSolicitation: phoneTrustedSolicitation ? 1 : 0,
          model: phoneModel || "",
          brand: phoneBrand || "",
          dialingCode: phoneDialingCode,
          number: phoneNumber,
        },
      },
      EntityRequest: {
        entityIdentifier,
      },
      PersonRequest: {
        birthDate: birthDate ? format(birthDate, "dd/MM/yyyy") : "",
        firstName,
        lastName,
        title,
      },
      IdentifierRequest: {
        identifierType: identifierType,
        identifierValue,
      },
    };

    try {
      const response = await updatePerson(requestData);
      
      toast.success(t("pages.account.updateSuccess"));
      onClose();
      
      setTimeout(() => {
        onSuccess();
      }, 500);
    } catch (err) {
      console.error("Error updating person:", err);
      const errorMessage = err instanceof Error ? err.message : "Erreur lors de la mise à jour";
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{t("pages.account.editTitle")}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Personal Information */}
          <Box>
            <Typography variant="h6" gutterBottom>
              {t("pages.account.personalInfo")}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(12, 1fr)' }, gap: 2 }}>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 2' } }}>
                <TextField
                  select
                  fullWidth
                  label={t("pages.account.civility")}
                  value={title}
                  onChange={(e) => setTitle(Number(e.target.value))}
                  size="small"
                >
                  <MenuItem value={1}>{t("pages.account.mr")}</MenuItem>
                  <MenuItem value={2}>{t("pages.account.mrs")}</MenuItem>
                </TextField>
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 5' } }}>
                <TextField
                  fullWidth
                  label={t("pages.account.firstName")}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  size="small"
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 5' } }}>
                <TextField
                  fullWidth
                  label={t("pages.account.lastName")}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  size="small"
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 5' } }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                  <DatePicker
                    label={t("pages.account.birthDate")}
                    value={birthDate}
                    onChange={(newValue: Date | null) => setBirthDate(newValue)}
                    format="dd/MM/yyyy"
                    open={datePickerOpen}
                    onOpen={() => setDatePickerOpen(true)}
                    onClose={() => setDatePickerOpen(false)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        helperText: "Format: JJ/MM/AAAA",
                        onClick: () => setDatePickerOpen(true)
                      }
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
          </Box>

          {/* Contact Information */}
          <Box>
            <Typography variant="h6" gutterBottom>
              {t("pages.account.contactInfo")}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(12, 1fr)' }, gap: 2 }}>
              <Box sx={{ gridColumn: 'span 12' }}>
                <TextField
                  fullWidth
                  label={t("pages.account.email")}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  size="small"
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailCompanySolicitation}
                      onChange={(e) => setEmailCompanySolicitation(e.target.checked)}
                      size="small"
                    />
                  }
                  label={t("pages.account.allowCompanySolicitation")}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={emailTrustedSolicitation}
                      onChange={(e) => setEmailTrustedSolicitation(e.target.checked)}
                      size="small"
                    />
                  }
                  label={t("pages.account.allowTrustedSolicitation")}
                />
              </Box>
            </Box>
          </Box>

          {/* Phone Information */}
          <Box>
            <Typography variant="h6" gutterBottom>
              {t("pages.account.mobile")}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(12, 1fr)' }, gap: 2 }}>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}>
                <Autocomplete
                  options={PHONE_CODES}
                  getOptionLabel={(option) => `+${option.code}`}
                  value={PHONE_CODES.find(pc => pc.code === phoneDialingCode) || null}
                  onChange={(event, newValue) => {
                    setPhoneDialingCode(newValue?.code || "");
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2">+{option.code}</Typography>
                        <Typography variant="caption" color="text.secondary">{option.country}</Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("pages.account.dialingCode")}
                      size="small"
                    />
                  )}
                  size="small"
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 9' } }}>
                <TextField
                  fullWidth
                  label={t("pages.account.phoneNumber")}
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  size="small"
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={phoneCompanySolicitation}
                      onChange={(e) => setPhoneCompanySolicitation(e.target.checked)}
                      size="small"
                    />
                  }
                  label={t("pages.account.allowCompanySolicitation")}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={phoneTrustedSolicitation}
                      onChange={(e) => setPhoneTrustedSolicitation(e.target.checked)}
                      size="small"
                    />
                  }
                  label={t("pages.account.allowTrustedSolicitation")}
                />
              </Box>
            </Box>
          </Box>

          {/* Address Information */}
          <Box>
            <Typography variant="h6" gutterBottom>
              {t("pages.account.address")}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(12, 1fr)' }, gap: 2 }}>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}>
                <TextField
                  fullWidth
                  label={t("pages.account.streetNumber")}
                  type="number"
                  value={address3Num}
                  onChange={(e) => setAddress3Num(e.target.value ? Number(e.target.value) : "")}
                  size="small"
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}>
                <Autocomplete
                  options={WAY_TYPES.filter(wt => wt.WAY_TYPE_NAME)}
                  getOptionLabel={(option) => option.WAY_TYPE_NAME || ""}
                  value={WAY_TYPES.find(wt => wt.WAY_TYPE_ID === address3Type.toString()) || null}
                  onChange={(event, newValue) => {
                    setAddress3Type(newValue ? Number(newValue.WAY_TYPE_ID) : 0);
                  }}
                  isOptionEqualToValue={(option, value) => option.WAY_TYPE_ID === value.WAY_TYPE_ID}
                  filterOptions={createFilterOptions({
                    matchFrom: 'start',
                    stringify: (option) => option.WAY_TYPE_NAME || '',
                  })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("pages.account.streetType")}
                      size="small"
                    />
                  )}
                  size="small"
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                <TextField
                  fullWidth
                  label={t("pages.account.streetName")}
                  value={address3Name}
                  onChange={(e) => setAddress3Name(e.target.value)}
                  size="small"
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                <TextField
                  fullWidth
                  label={t("pages.account.zipCode")}
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  size="small"
                  InputProps={{
                    endAdornment: postalLoading ? (
                      <InputAdornment position="end">
                        <CircularProgress size={16} />
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                <Autocomplete
                  freeSolo
                  options={citySuggestions}
                  value={city}
                  onInputChange={(_event, newValue) => setCity(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("pages.account.city")}
                      size="small"
                    />
                  )}
                  size="small"
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                <TextField
                  fullWidth
                  label={t("pages.account.address1")}
                  value={address1}
                  onChange={(e) => setAddress1(e.target.value)}
                  size="small"
                  placeholder="Appartement, étage"
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                <TextField
                  fullWidth
                  label={t("pages.account.address2")}
                  value={address2}
                  onChange={(e) => setAddress2(e.target.value)}
                  size="small"
                  placeholder="Entrée, bâtiment"
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 8' } }}>
                <TextField
                  fullWidth
                  label={t("pages.account.address4")}
                  value={address4}
                  onChange={(e) => setAddress4(e.target.value)}
                  size="small"
                  placeholder="Lieu dit"
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}>
                <Autocomplete
                  options={COUNTRIES}
                  value={country}
                  onChange={(event, newValue) => {
                    setCountry(newValue || "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("pages.account.country")}
                      size="small"
                    />
                  )}
                  size="small"
                  freeSolo
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={addressCompanySolicitation}
                      onChange={(e) => setAddressCompanySolicitation(e.target.checked)}
                      size="small"
                    />
                  }
                  label={t("pages.account.allowCompanySolicitation")}
                />
              </Box>
              <Box sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={addressTrustedSolicitation}
                      onChange={(e) => setAddressTrustedSolicitation(e.target.checked)}
                      size="small"
                    />
                  }
                  label={t("pages.account.allowTrustedSolicitation")}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !firstName || !lastName || !email}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? t("common.saving") : t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAccountDialog;
